"use client";

import React, {useMemo, useState} from "react";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import {X} from "lucide-react";
import {cn} from "@root/server/client/esweb/lib/utils";
import type {UploadedFile} from "./galleryTypes";
import {buildPreviewUrl, normalizeUrl} from "./galleryUtils";
import {GalleryImageWithLoader} from "./GalleryImageWithLoader";

interface UploadGalleryProps {
    files: UploadedFile[];
    className?: string;
    emptyState?: React.ReactNode;
    onRemove?: (file: UploadedFile) => void | Promise<void>;
    removingIds?: Set<string>;
}

export function UploadGallery({
    files,
    className,
    emptyState,
    onRemove,
    removingIds,
}: UploadGalleryProps) {
    const [lightboxIndex, setLightboxIndex] = useState<number>(0);
    const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);

    const slides = useMemo(
        () => files.map((file) => ({src: normalizeUrl(file.url), title: file.name})),
        [files]
    );

    if (files.length === 0) {
        return (
            <div className={cn("flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 p-10 text-center text-muted-foreground", className)}>
                {emptyState ?? <p>Ještě jste nenahráli žádné fotografie.</p>}
            </div>
        );
    }

    return (
        <div className={cn("grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4", className)}>
            {files.map((file, index) => {
                const isRemoving = removingIds?.has(file.id);
                return (
                    <div key={file.id} className="group relative overflow-hidden rounded-lg border bg-muted">
                        <button
                            type="button"
                            className="absolute right-1 top-1 z-10 rounded-full bg-background/80 p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                            onClick={() => {
                                if (!onRemove || isRemoving) return;
                                onRemove(file);
                            }}
                            disabled={isRemoving}
                            aria-label={`Odstranit ${file.name}`}
                        >
                            <X className={cn("h-4 w-4", isRemoving && "animate-pulse")} />
                        </button>
                        <button
                            type="button"
                            className="relative block h-full w-full overflow-hidden"
                            onClick={() => {
                                setLightboxIndex(index);
                                setLightboxOpen(true);
                            }}
                            aria-label={`Otevřít náhled ${file.name}`}
                        >
                            <GalleryImageWithLoader
                                src={buildPreviewUrl(file.url)}
                                alt={file.name}
                                wrapperClassName="block h-full w-full"
                                className="aspect-square h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                        </button>
                    </div>
                );
            })}

            <Lightbox
                open={lightboxOpen}
                close={() => setLightboxOpen(false)}
                slides={slides}
                index={lightboxIndex}
            />
        </div>
    );
}

